import {evaluate, isBoolean} from "mathjs";
import { Object3D } from "three";
import Scenario from "./scenarioElements/Scenario";
import { MetadataManager } from "./MetadataManager";
import Engine from "./Engine";
import Store from "../state/store";
import { ISubscriptionToVariable, ISubscriptions, ITag } from "../interfaces/Iot";
import { IMetaDataObject, ITextVariablesParams } from "../interfaces/Misc";
import { BuiltInTextVariables } from "../interfaces/enums";

export default class TextVariables 
{
	static variables = {
		[BuiltInTextVariables.ProjectName]: () => { return Scenario.current()?.name; },
		[BuiltInTextVariables.StepName]: (params: ITextVariablesParams) => 
		{
			if (params && params.stepId) { return Scenario.getStep(params.stepId)?.name ?? ""; }
			else { return Scenario.currentStep()?.name; }
		},
		[BuiltInTextVariables.StepNumber]: (params: ITextVariablesParams) => 
		{
			const step = params && params.stepId ? Scenario.getStep(params.stepId) : Scenario.currentStep();
			if(!step) return "";

			const isInitialSequence = step && Scenario.initialSequence()?.id === step?.sequence.id;

			if(isInitialSequence)
			{
				if(step.isTrackersStep())
				{
					return "";
				}
				else if (step.isBeforeTrackersStep())
				{
					return step.getIndex() + 1;
				}
				else
				{
					return step.getIndex();
				}
			}
			else
			{
				return step.getIndex() + 1;
			}
		},
		[BuiltInTextVariables.SequenceName]: (params: ITextVariablesParams) => 
		{
			if (params && params.sequenceId) { return Scenario.getSequence(params.sequenceId)?.name ?? ""; }
			else { return Scenario.currentSequence()?.name; }
		},
		[BuiltInTextVariables.StepTotal]: (params: ITextVariablesParams) => 
		{
			const sequence = params && params.sequenceId ? Scenario.getSequence(params.sequenceId) : Scenario.currentSequence();
			const isInitialSequence = sequence && Scenario.initialSequence()?.id === sequence?.id;
			return (isInitialSequence ? sequence?.steps.length -1 : sequence?.steps.length) ?? "";
		},
		// "TotalStepCount" : ()=>{return Scenario.current().countSteps()},
		[BuiltInTextVariables.ObjectName]: (params: ITextVariablesParams) => 
		{
			if (params && params.partName) { return params.partName; }
			else { return "Not a Part"; }
		},
	}

	static getVariables(metaData: IMetaDataObject | null = null)
	{
		const subscribedTags: ISubscriptions = Store.getState().iotModule.subscriptionKeys;
		const subscriptionKeysToVariables: ISubscriptionToVariable = Store.getState().iotModule.subscriptionKeysToVariables;

		const iotVariables = {};
		let metadataVariables = {};

		for(const [key, value] of Object.entries(subscribedTags))
		{
			iotVariables[subscriptionKeysToVariables[key]] = ()=>{return TextVariables.getSubscribedTagValue(value)};
		}

		if(metaData)
		{
			metadataVariables = TextVariables.getObjectMetadataVariables(metaData);
		}

		return {...TextVariables.variables, ...iotVariables, ...metadataVariables};
	}

	static getSubscribedTagValue(variableIdentifiers: ITag)
	{
		const tags = Store.getState().iotModule.tags;
		if(!tags) return null;

		for(const tag of tags)
		{
			if(tag.topic === variableIdentifiers.topic && tag.name === variableIdentifiers.name)
			{
				return tag.value;
			}
		}
		return null;
	}

	static getObjectMetadataVariables(metadata: IMetaDataObject) 
	{
		const metadataVariables = {};
		for(const key in metadata) 
		{
			const parsedKey = key.replaceAll(TextVariables.bannedCharacters, '');
			const newVariableName = "meta" + parsedKey;
			metadataVariables[newVariableName] = (params) => { return params.metaData[key].value;};
		}
		return metadataVariables;
	}

	static variableMarker = '#';
	static bannedCharacters = /[^a-zA-Z0-9_]+/gm;
	static allowedCharacters = /[a-zA-Z0-9_]+/gm;

	//params can be an object containing params for variables
	//stepId (specific step id to access)
	//sequenceId (specific sequence id to access)
	//partName (specific part name)
	static getVariableData(variable, params: ITextVariablesParams | null = null)
	{
		const variables = TextVariables.getVariables(params?.metaData);
		if (variables[variable] && !Scenario.currentStep()?.isDefault() && !Scenario.currentStep()?.isTrackersStep() ) 
		{
			return variables[variable](params);
		}
		return TextVariables.variableMarker + variable;
	}

	static getVariableFullData(word: string, splitter: string, params: ITextVariablesParams | null = null)
	{
		if(splitter.endsWith(TextVariables.variableMarker))
		{
			return {word: TextVariables.getVariableData(word, params), splitter: splitter.slice(0, -1)}
		}
		return {word: word, splitter: splitter};
	}

	static breakToWords(text: string) 
	{
		if(text === undefined || text === null) text = '';

		if(!text.split)
		{
			console.warn("Text Variables > breakToWords is trying to split a questionable text", text);
			//if the text is not empty but also cannot be split, it could be an object. Convert it to text and try to continue
			text = JSON.stringify(text);
		}

		const words = text.split(TextVariables.bannedCharacters);
		if (words[0] === '') words.shift();

		const splitters = text.split(TextVariables.allowedCharacters);
		if (splitters[splitters.length - 1] === '') splitters.pop();

		return { words: words, splitters: splitters };
	}

	static buildFromWords(wordArray: string[], splitters: string[]) 
	{
		let text = '';
		for (let i = 0; i < wordArray.length; i++) 
		{
			text += splitters[i] + wordArray[i];
		}

		return text;
	}

	//returns the currently editing word in the text based on caret index, returns null if the word doesnt start with TextVariables.variableMarker
	static getCurrentWord(text: string, caretIndex: number) 
	{
		const textObject = TextVariables.breakToWords(text);
		let charCounter = 0;
		let currentWord = '';
		let currentSplitter = '';
		for (let i = 0; i < textObject.words.length; i++) 
		{
			charCounter += textObject.words[i].length + textObject.splitters[i].length;
			if (caretIndex <= charCounter) 
			{
				currentWord = textObject.words[i];
				currentSplitter = textObject.splitters[i];
				break;
			}
		}

		if (!currentSplitter.endsWith(TextVariables.variableMarker)) return null;
		return currentWord;
	}

	//returns an array of commands that start with word, if word is null returns empty array, if word is empty returns all the commands
	static autoComplete(word: string, includedParts: Object3D[] | null = null) 
	{
		const result: string[] = [];
		let keys: string[];
		const selectionRoot: Object3D = Engine.getInstance().getSelectionRoot();
		if(includedParts && includedParts.length === 1)
		{
			if(selectionRoot && selectionRoot.userData.guid === includedParts[0].userData.guid)
			{
				const metaDataKeys = MetadataManager.metadataKeys;
				const metaData = {};
				for(const key of metaDataKeys)
				{
					metaData[key] = {value: ""};
				}
				keys = Object.keys(TextVariables.getVariables(metaData));
			}
			else
			{
				const metaData = includedParts[0]?.userData?.metaData ?? null;
				keys = Object.keys(TextVariables.getVariables(metaData));
			}
		}
		else
		{
			keys = Object.keys(TextVariables.getVariables());
		}
		 
		for (const variable of keys) 
		{
			if (word === null) return result;
			if (!word || (variable.toLocaleLowerCase().startsWith(word.toLocaleLowerCase()) && variable.toLocaleLowerCase() !== word.toLocaleLowerCase())) 
			{
				if (includedParts && (includedParts.length > 0 || isBoolean(includedParts)))
				{
					result.push(variable);
				}
				else if (!variable.toLocaleLowerCase().startsWith('object')) 
				{
					result.push(variable);
				}
			}
		}

		return result;
	}

	static textToData(text: string, params: ITextVariablesParams | null = null) 
	{
		const textObject = TextVariables.breakToWords(text);
		for (let i = 0; i < textObject.words.length; i++) 
		{
			if (textObject.splitters[i].endsWith(TextVariables.variableMarker)) 
			{
				textObject.splitters[i] = textObject.splitters[i].replace(TextVariables.variableMarker, '');
				textObject.words[i] = TextVariables.getVariableData(textObject.words[i], params);
			}
		}

		return TextVariables.buildFromWords(textObject.words, textObject.splitters);
	}

	static evaluateExpression(expression: string)
	{
		const expressionObject = TextVariables.breakToWords(expression);
		const wordsAmount = expressionObject.words.length;

		for(let i = 0; i < wordsAmount; i++)
		{
			const variableData = TextVariables.getVariableFullData(expressionObject.words[i], expressionObject.splitters[i]);
			expressionObject.words[i] = variableData.word;
			expressionObject.splitters[i] = variableData.splitter;
		}
		
		const tempSplitters = [...expressionObject.splitters];
		const tempWords = [...expressionObject.words];
		const tempStringWords: string[] = [];

		for(let i = 0; i < wordsAmount ; i++)
		{
			tempStringWords.push(tempWords[i] === '' ? '' : `"${tempWords[i]}"`);
			tempSplitters[i] = tempSplitters[i].replaceAll(' ', '');
			tempSplitters[i] = tempSplitters[i].replaceAll(/^=/g, '==');
			while(tempSplitters[i].includes('==='))
			{
				tempSplitters[i] = tempSplitters[i].replaceAll('===', '==')
			}
		}

		expressionObject.splitters = tempSplitters;

		const processedExpression = TextVariables.buildFromWords(expressionObject.words, expressionObject.splitters);

		let solution: string;
		try
		{
			solution = evaluate(processedExpression);
			return solution;
		}
		catch
		{
			try
			{
				const processedStringExpression = TextVariables.buildFromWords(tempStringWords, expressionObject.splitters);
				solution = eval(processedStringExpression);
				if(solution.toString() === 'NaN' || typeof(solution) !== 'boolean') solution = TextVariables.textToData(expression);
				return solution;
			}
			catch(e)
			{
				return TextVariables.textToData(expression);
			}
		}
	}
}
